// The tenant role vocabulary, mirroring tenant-backend/services/permissions.py.
//
// It lives in one file because the users list, the invite dialog, the pending-invitation list and
// the SSO just-in-time picker are read together during an access review: a role that is
// "Administrator" on one and "Tenant Administrator" on another reads as two different grants.

export type TenantRole = 'viewer' | 'it_user' | 'administrator' | 'owner'

// Least authority first. A control that renders this list in order opens on the role that can do
// the least, which is what makes the default safe rather than merely conventional.
export const ASSIGNABLE_ROLES: TenantRole[] = ['viewer', 'it_user', 'administrator', 'owner']

export const LEAST_PRIVILEGED_ROLE: TenantRole = ASSIGNABLE_ROLES[0]

export const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  it_user: 'IT User',
  administrator: 'Administrator',
  owner: 'Owner',
  super_admin: 'Splashtop Administrator',
}

// Described in the product's own verbs. A job title tells an inviter nothing about what the person
// will be able to do, and this is the moment the inviter decides.
export const ROLE_DESCRIPTIONS: Record<string, string> = {
  viewer: 'Can see packages and packaging jobs. Cannot change anything.',
  it_user: 'Everything a Viewer can do, and can create, deploy and remove packages.',
  administrator:
    'Everything an IT User can do, and can invite people, set roles, configure integrations and change tenant settings.',
  owner: 'Everything an Administrator can do, and can buy, change and cancel the subscription.',
}

export const roleLabel = (role: string): string => ROLE_LABELS[role] || role

// Colour tracks authority, so the badge says how much power a row carries without being read.
export const roleBadgeClass = (role: string): string => {
  switch (role) {
    case 'owner':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
    case 'administrator':
    case 'super_admin':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
    case 'it_user':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400'
  }
}
